"use client";

import { cn } from "@/lib/utils";
import { createClient } from "@/lib/supabase/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import Link from "next/link";
import { useI18n } from "./i18n-provider";
import { useState, useEffect } from "react";
import { useTheme } from "next-themes";
import Image from "next/image";
import { trackEvent } from "@/lib/analytics";
import { getDefaultPostLoginPath } from "@/lib/auth/post-login-path";
import {
  OAUTH_NEXT_COOKIE_MAX_AGE_SECONDS,
  OAUTH_NEXT_COOKIE_NAME,
} from "@/lib/auth/oauth-constants";

type SignUpFormProps = React.ComponentPropsWithoutRef<"div"> & {
  redirectTo?: string;
};

export function SignUpForm({
  className,
  redirectTo,
  ...props
}: SignUpFormProps) {
  const [error, setError] = useState<string | null>(null);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { locale, dictionary } = useI18n();
  const t = dictionary.authForms.signUp;
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    trackEvent("view_signup", {
      locale,
      ...(redirectTo ? { redirect: redirectTo } : {}),
    });
    // Fire once per mount; locale/redirect are stable for a page view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const googleButtonSrc =
    resolvedTheme === "dark"
      ? "/google/dark/web_dark_rd_SU.svg"
      : "/google/light/web_light_rd_SU.svg";

  const handleGoogleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (!isGoogleLoading) {
        handleGoogleSignUp();
      }
    }
  };

  const handleGoogleSignUp = async () => {
    const supabase = createClient();
    setIsGoogleLoading(true);
    setError(null);

    try {
      const next = redirectTo ?? getDefaultPostLoginPath(locale);
      document.cookie = `${OAUTH_NEXT_COOKIE_NAME}=${encodeURIComponent(next)}; path=/; max-age=${OAUTH_NEXT_COOKIE_MAX_AGE_SECONDS}; samesite=lax`;
      trackEvent("oauth_start", { source: "signup", locale });
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth/oauth?next=${encodeURIComponent(next)}`,
        },
      });

      if (error) {
        throw error;
      }
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : "An error occurred");
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{t.title}</CardTitle>
          <CardDescription>{t.description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-6 items-center">
            {error && <p className="text-sm text-brand-primary">{error}</p>}
            {isGoogleLoading ? (
              <span className="text-sm text-center text-muted-foreground">
                {t.connecting}
              </span>
            ) : mounted ? (
              <Image
                src={googleButtonSrc}
                alt="Sign up with Google"
                width={179}
                height={40}
                className="cursor-pointer"
                onClick={handleGoogleSignUp}
              />
            ) : (
              <div style={{ width: 179, height: 40 }} />
            )}
          </div>
          <div className="mt-4 text-center text-sm">
            {t.alreadyHaveAccount}{" "}
            <Link
              href={
                redirectTo
                  ? `/${locale}/sign-in?redirect=${encodeURIComponent(redirectTo)}`
                  : `/${locale}/sign-in`
              }
              className="underline underline-offset-4"
            >
              {t.loginLink}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
